/**
 * `<ChatSessionList>` — the session-history sidebar: list, select, rename, new (editor-chat.md
 * §2.4, §5 step 16).
 *
 * editor-chat.md §2.4 / §5 step 16: "session history, rename, save-as-note action". This
 * component is the history + rename + new-session half (the save-as-note action lives on the
 * `<ChatSurface>`'s active-session header — it acts on the *open* session). It renders the
 * sessions from `GET /chat/sessions`, lets the user pick one, rename it inline, or start a
 * fresh one.
 *
 * The non-model affordances (this list, rename, open) keep working when the chat model is
 * unavailable (editor-chat.md §1.1 / §1.3 — "the chat must keep its non-model affordances ...
 * working when the model key is missing"). This component makes no model calls — it is purely
 * session CRUD over `chatApi`'s REST endpoints.
 *
 * Pure presentational + local rename-draft state — the surface owns the data fetching and the
 * mutations (it passes the sessions in and the callbacks down).
 */

import { useState } from 'react';
import { formatRelativeTime } from './sessionTime';
import type { ChatSessionSummary } from './chatTypes';

/** `<ChatSessionList>` props — the sessions + the selection/rename/new callbacks. */
export interface ChatSessionListProps {
  /** The session summaries to list — most-recently-updated first (the backend orders them). */
  sessions: ChatSessionSummary[];
  /** The currently-open session's id — `null` when a fresh, unsent session is being composed. */
  activeSessionId: string | null;
  /** True while the session list is loading. */
  isLoading: boolean;
  /** Open a session by id — the surface fetches its history and shows it. */
  onSelect: (sessionId: string) => void;
  /** Start a fresh session — the surface clears the active session; the first turn creates it. */
  onNew: () => void;
  /** Rename a session — the surface calls `renameChatSession` and refetches the list. */
  onRename: (sessionId: string, title: string) => void;
}

/**
 * The session-history sidebar. Lists sessions, supports inline rename, and offers a
 * "new chat" action. All of it works without the chat model — it is pure session CRUD
 * (editor-chat.md §1.3).
 */
export function ChatSessionList({
  sessions,
  activeSessionId,
  isLoading,
  onSelect,
  onNew,
  onRename,
}: ChatSessionListProps): JSX.Element {
  // The id of the session being renamed inline, and the rename draft text.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');

  const startRename = (session: ChatSessionSummary): void => {
    setRenamingId(session.id);
    setRenameDraft(session.title);
  };

  const commitRename = (): void => {
    if (renamingId !== null) {
      const trimmed = renameDraft.trim();
      if (trimmed !== '') onRename(renamingId, trimmed);
    }
    setRenamingId(null);
    setRenameDraft('');
  };

  // facelift-v3: restyle to the design's day-grouped session cards. The list
  // is bucketed by day ("Today" / "Yesterday" / a short M/D label) from each
  // session's updatedAt (falling back to createdAt). Backend already orders
  // sessions most-recent-first, so the buckets come out in order as we scan.
  // Perf E3: the list rows are slim ChatSessionSummary — the preview + turn
  // count are computed server-side (the message bodies no longer ship). The
  // design's .session-card__wf (workflow badge) still has no backing field,
  // so it is intentionally not rendered.
  const groups = !isLoading ? groupSessionsByDay(sessions) : [];

  return (
    <aside className="chat-session-list chat-side" data-testid="chat-session-list">
      <div className="chat-session-list__header">
        <span className="chat-session-list__title label-mono">Recent</span>
        <button
          type="button"
          className="chat-session-list__new btn btn--cool btn--xs"
          onClick={onNew}
        >
          New
        </button>
      </div>

      {isLoading ? (
        <div className="chat-session-list__loading">Loading sessions…</div>
      ) : sessions.length === 0 ? (
        <div className="chat-session-list__empty">
          No chats yet — start one with the composer.
        </div>
      ) : (
        <ul className="chat-session-list__items">
          {groups.map((group) => (
            <li key={group.label} style={{ listStyle: 'none' }}>
              <div className="session-day">{group.label}</div>
              {group.sessions.map((session) =>
                renamingId === session.id ? (
                  // Inline rename — commit on blur or Enter, cancel on Escape.
                  <input
                    key={session.id}
                    className="session-card__rename"
                    value={renameDraft}
                    autoFocus
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename();
                      if (e.key === 'Escape') {
                        setRenamingId(null);
                        setRenameDraft('');
                      }
                    }}
                    aria-label="Rename chat session"
                  />
                ) : (
                  <button
                    key={session.id}
                    type="button"
                    className={`session-card${
                      session.id === activeSessionId ? ' session-card--active' : ''
                    }`}
                    onClick={() => onSelect(session.id)}
                    // Double-click to rename — a low-friction, discoverable affordance.
                    onDoubleClick={() => startRename(session)}
                  >
                    <div className="session-card__row1">
                      <span className="session-card__title">{session.title}</span>
                      {/* A saved-as-note session shows a marker — the user knows it is durable. */}
                      {session.savedAsNoteId !== null && (
                        <span className="session-card__saved-badge" title="Saved as a note">
                          ●
                        </span>
                      )}
                    </div>
                    {/* Real-data preview: server-computed snippet of the newest
                        text-bearing message (perf E3 — no message bodies here). */}
                    {(session.preview ?? '') !== '' && (
                      <div className="session-card__preview">{session.preview}</div>
                    )}
                    <div className="session-card__foot">
                      <span>{formatRelativeTime(session)}</span>
                      {(session.turnCount ?? 0) > 0 && (
                        <>
                          <span aria-hidden="true">·</span>
                          <span>
                            {session.turnCount} {session.turnCount === 1 ? 'turn' : 'turns'}
                          </span>
                        </>
                      )}
                    </div>
                  </button>
                ),
              )}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

/** A day bucket of sessions — `label` is the .session-day header text. */
interface SessionDayGroup {
  label: string;
  sessions: ChatSessionSummary[];
}

/**
 * Bucket sessions into day groups ("Today" / "Yesterday" / "M/D") keyed off
 * `updatedAt` (falling back to `createdAt`). Preserves the backend's
 * most-recent-first order: groups appear in first-seen order, sessions stay
 * in their incoming order within each group.
 */
function groupSessionsByDay(sessions: ChatSessionSummary[]): SessionDayGroup[] {
  const groups: SessionDayGroup[] = [];
  const byLabel = new Map<string, SessionDayGroup>();
  for (const session of sessions) {
    const label = dayLabel(session.updatedAt || session.createdAt);
    let group = byLabel.get(label);
    if (group === undefined) {
      group = { label, sessions: [] };
      byLabel.set(label, group);
      groups.push(group);
    }
    group.sessions.push(session);
  }
  return groups;
}

/** "Today" / "Yesterday" / a short `M/D` label for the day-group header. */
function dayLabel(ts: string | undefined): string {
  if (!ts) return 'Earlier';
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return 'Earlier';
  const startOfDay = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000);
  if (dayDiff <= 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

// formatRelativeTime moved to ./sessionTime (U7 2026-07-05) — it is shared with the chat
// surface's idle state, and exporting a non-component from this file breaks fast refresh.
